import * as THREE from 'three'
import * as CANNON from 'cannon-es'

// ─── D6 ──────────────────────────────────────────────────────────────────────
const D6_FACE_NORMALS = [
    { normal: new CANNON.Vec3( 0,  1,  0), value: 1 },
    { normal: new CANNON.Vec3( 0, -1,  0), value: 6 },
    { normal: new CANNON.Vec3( 1,  0,  0), value: 2 },
    { normal: new CANNON.Vec3(-1,  0,  0), value: 5 },
    { normal: new CANNON.Vec3( 0,  0,  1), value: 3 },
    { normal: new CANNON.Vec3( 0,  0, -1), value: 4 },
]

function makeD6Texture(number) {
    const size = 256
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = size
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#f5f0e0'
    ctx.beginPath()
    ctx.roundRect(2, 2, size - 4, size - 4, 28)
    ctx.fill()
    ctx.strokeStyle = '#3a3020'
    ctx.lineWidth = 7
    ctx.stroke()
    ctx.fillStyle = number === 1 ? '#cc1111' : '#1a1a1a'
    ctx.font = `bold ${Math.round(size * 0.64)}px Georgia, serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(number), size / 2, size / 2 + 4)
    return new THREE.CanvasTexture(canvas)
}

// ─── D20 ──────────────────────────────────────────────────────────────────────
const PHI  = (1 + Math.sqrt(5)) / 2
const NORM = Math.sqrt(1 + PHI * PHI)

const I_VERTS = [
    [-1,  PHI, 0], [1,  PHI, 0], [-1, -PHI, 0], [1, -PHI, 0],
    [0, -1,  PHI], [0,  1,  PHI], [0, -1, -PHI], [0,  1, -PHI],
    [PHI, 0, -1], [PHI, 0,  1], [-PHI, 0, -1], [-PHI, 0,  1],
].map(v => [v[0] / NORM, v[1] / NORM, v[2] / NORM])

const I_FACES = [
    [0,11,5],[0,5,1],[0,1,7],[0,7,10],[0,10,11],
    [1,5,9],[5,11,4],[11,10,2],[10,7,6],[7,1,8],
    [3,9,4],[3,4,2],[3,2,6],[3,6,8],[3,8,9],
    [4,9,5],[2,4,11],[6,2,10],[8,6,7],[9,8,1],
]

const D20_FACE_NORMALS = I_FACES.map((face, i) => {
    const [a, b, c] = face.map(vi => I_VERTS[vi])
    const cx = (a[0] + b[0] + c[0]) / 3
    const cy = (a[1] + b[1] + c[1]) / 3
    const cz = (a[2] + b[2] + c[2]) / 3
    const len = Math.sqrt(cx*cx + cy*cy + cz*cz)
    return { normal: new CANNON.Vec3(cx/len, cy/len, cz/len), value: i + 1 }
})

function makeD20Texture(number) {
    const size = 256
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = size
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, size, size)
    const pad = 8
    ctx.fillStyle = '#dce8ff'
    ctx.beginPath()
    ctx.moveTo(size / 2, pad)
    ctx.lineTo(size - pad, size - pad)
    ctx.lineTo(pad, size - pad)
    ctx.closePath()
    ctx.fill()
    ctx.strokeStyle = '#1a2a5e'
    ctx.lineWidth = 6
    ctx.stroke()
    const fontSize = number > 9 ? Math.round(size * 0.38) : Math.round(size * 0.46)
    ctx.fillStyle = number <= 2 ? '#cc1111' : '#1a1a2e'
    ctx.font = `bold ${fontSize}px Georgia, serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(number), size / 2, size * 0.64)
    return new THREE.CanvasTexture(canvas)
}

// ─── Dice class ───────────────────────────────────────────────────────────────
export class Dice {
    constructor({ scene, world, physicsMaterial, position = { x: 0, y: 1, z: 0 }, size = 1, type = 'd6' }) {
        this.scene  = scene
        this.world  = world
        this.size   = size
        this.type   = type
        this.isThrown   = false
        this.isSettled  = false
        this._initialFall = true
        this.onResult = null
        this._buildMesh()
        this._buildBody(physicsMaterial, position)
        this.body.addEventListener('sleep', () => this._onSleep())
    }

    _buildMesh() {
        if (this.type === 'd6') {
            const faceValues = [2, 5, 1, 6, 3, 4]
            const materials = faceValues.map(n => new THREE.MeshStandardMaterial({
                map: makeD6Texture(n), roughness: 0.25, metalness: 0.05,
            }))
            this.mesh = new THREE.Mesh(new THREE.BoxGeometry(this.size, this.size, this.size), materials)
        } else {
            const r = this.size * 0.65
            const geo = new THREE.IcosahedronGeometry(r, 0)
            for (let i = 0; i < 20; i++) geo.addGroup(i * 3, 3, i)
            // Map each triangular face to the same UV triangle so the texture shows fully
            const uvArr = []
            for (let i = 0; i < 20; i++) uvArr.push(0.5, 0.92, 0.05, 0.06, 0.95, 0.06)
            geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uvArr), 2))
            const materials = Array.from({ length: 20 }, (_, i) => new THREE.MeshStandardMaterial({
                map: makeD20Texture(i + 1), roughness: 0.3, metalness: 0.05, flatShading: true,
            }))
            this.mesh = new THREE.Mesh(geo, materials)
        }
        this.mesh.castShadow = true
        this.mesh.userData.dice = this
        this.scene.add(this.mesh)
    }

    _buildBody(physicsMaterial, position) {
        let shape
        if (this.type === 'd6') {
            const h = this.size * 0.5
            shape = new CANNON.Box(new CANNON.Vec3(h, h, h))
        } else {
            const r = this.size * 0.65
            const verts = I_VERTS.map(v => new CANNON.Vec3(v[0] * r, v[1] * r, v[2] * r))
            shape = new CANNON.ConvexPolyhedron({ vertices: verts, faces: I_FACES })
        }
        this.body = new CANNON.Body({
            mass: 1,
            material: physicsMaterial,
            shape,
            sleepSpeedLimit: 0.15,
            sleepTimeLimit: 0.6,
        })
        this.body.position.set(position.x, position.y, position.z)
        this.world.addBody(this.body)
    }

    throwInDirection(worldDir, power) {
        if (this.isThrown && !this.isSettled) return false
        this.isThrown  = true
        this.isSettled = false
        this._initialFall = false
        this.body.wakeUp()
        this.body.velocity.set(0, 0, 0)
        this.body.angularVelocity.set(0, 0, 0)
        const speed  = 4 + power * 10
        const upward = 6 + power * 4
        this.body.applyImpulse(new CANNON.Vec3(worldDir.x * speed, upward, worldDir.z * speed))
        const spin = power * 35
        this.body.angularVelocity.set(
            (Math.random() - 0.5) * spin,
            (Math.random() - 0.5) * spin,
            (Math.random() - 0.5) * spin,
        )
        return true
    }

    _onSleep() {
        if (this._initialFall) {
            this._initialFall = false
            this.isSettled = true  // mark resting so auto-throw can start
            return
        }
        if (!this.isThrown) return
        this.isSettled = true
        const value = this._detectFace()
        if (this.onResult) this.onResult(value)
    }

    _detectFace() {
        const normals = this.type === 'd6' ? D6_FACE_NORMALS : D20_FACE_NORMALS
        const localUp = this.body.quaternion.inverse().vmult(new CANNON.Vec3(0, 1, 0))
        let best = -Infinity, result = 1
        for (const { normal, value } of normals) {
            const dot = normal.dot(localUp)
            if (dot > best) { best = dot; result = value }
        }
        return result
    }

    update() {
        this.mesh.position.copy(this.body.position)
        this.mesh.quaternion.copy(this.body.quaternion)
    }

    dispose() {
        this.mesh.geometry.dispose()
        const mats = Array.isArray(this.mesh.material) ? this.mesh.material : [this.mesh.material]
        mats.forEach(m => { m.map?.dispose(); m.dispose() })
        this.scene.remove(this.mesh)
        this.world.removeBody(this.body)
    }
}
